import React, { useState, memo } from 'react';
import styles from './OrdersScreenComponent.module.scss';
import { RouteComponentProps, useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';
import { OrdersGridItemData, OrdersScreenProps } from './OrdersScreen';
import { Template } from 'devextreme-react';
import StateOrderSimpleTextColumnTemplate from '../../templates/dataGrid/column/state-order-simple-text-column-template/StateOrderSimpleTextColumnTemplate';
import EditedBySimpleTextColumnTemplate from '../../templates/dataGrid/column/edited-by-simple-text-column-template/EditedBySimpleTextColumnTemplate';
import TruckFillingColumnTemplate from '../../templates/dataGrid/column/truck-filling-column-template/TruckFillingColumnTemplate';
import { Box, Item } from 'devextreme-react/box';
import DataGrid from 'fulogics-ui-web/dist/layout/dx/DataGrid/DataGrid';
import {
    orderScreenButtonId,
    ordersScreenId,
    orderScreenOrderInfoDrawerId,
} from './inspectorOptions';
import ForwardStepsLocationsColumnTemplate from '../../templates/dataGrid/column/forward-steps-locations-column-template/ForwardStepsLocationsColumnTemplate';
import ReturnStepsLocationsColumnTemplate from '../../templates/dataGrid/column/return-steps-locations-column-template/ReturnStepsLocationsColumnTemplate';
import { PATH_ORDER_EDIT, PATH_ORDER_NEW } from '../../../constants/routerPaths';
import Button from 'fulogics-ui-web/dist/layout/dx/Button/Button';
import Drawer from 'fulogics-ui-web/dist/layout/dx/Drawer/Drawer';
import OrderDetailsDrawerContent from './OrderDetailsDrawerContent';
import DraftClosedDeadlineColumnTemplate from '../../templates/dataGrid/column/draft-closed-deadline-column-template/DraftClosedDeadlineColumnTemplate';
import { confirm } from 'devextreme/ui/dialog';
import useMemoDataSource from 'fulogics-ui-web/dist/layout/hooks/useMemoDataSource';
import { useVStore } from 'fulogics-ui-web/dist/layout/hooks/useVStore';
import InspectorVariablesContext from 'fulogics-ui-web/dist/layout/inspector/context/InspectorVariablesContext';
import { IdType } from 'fulogics-ui-core/dist/api/models/IdType';
import WmsExternalIdsColumnTemplate from '../../templates/dataGrid/column/wms-external-ids-column-template/WmsExternalIdsColumnTemplate';

export interface OrdersScreenComponentState {
    selectedRow?: OrdersGridItemData;
}

const OrdersScreenComponent = memo((props: OrdersScreenComponentProps) => {
    const { t } = useTranslation('OrdersScreenComponent');
    const { push } = useHistory();
    const [selectedRow, setSelectedRow] = useState<OrdersGridItemData | undefined>();

    const ordersDataSourceOptions = useMemoDataSource(
        () => props.createOrdersDataSource?.(),
        [props.createOrdersDataSource]
    );

    const onCreateNewOrder = () => {
        push(PATH_ORDER_NEW);
    };

    const handleCellClick = (e: { row?: { data?: OrdersGridItemData } }) => {
        if (e?.row?.data) {
            setSelectedRow(e?.row?.data);
        }
    };

    const handleModalClose = () => {
        setSelectedRow(undefined);
    };

    const handleRemoveItem = (key: any) => {
        confirm(`<i>${t('deleteModal.content')}</i>`, `${t('deleteModal.title')}`).then(
            (result) => {
                if (result) {
                    ordersDataSourceOptions
                        ?.store()
                        ?.remove?.(key)
                        ?.then(() => {
                            handleModalClose();
                        });
                }
            }
        );
    };

    const vstore = useVStore(
        () => ({
            callbacks: {
                orderErrorOnEditClick: (orderId?: IdType) => push(`order-edit/${orderId}`),
            },
        }),
        [push]
    );

    const showInfoDrawer = !!selectedRow;
    const drawerContextData = {
        orderItem: selectedRow,
        currentUserId: props.currentUserId,
        removeItem: handleRemoveItem,
    };

    return (
        <Drawer
            inspectorId={orderScreenOrderInfoDrawerId}
            opened={showInfoDrawer}
            component={OrderDetailsDrawerContent}
            drawerContextData={drawerContextData}
            onCloseDrawerClick={handleModalClose}
        >
            <div className={styles.container}>
                <InspectorVariablesContext store={vstore}>
                    <Box width="100%" height="100%" direction="col">
                        <Item ratio={1}>
                            <DataGrid
                                dataSource={ordersDataSourceOptions}
                                inspectorId={ordersScreenId}
                                repaintChangesOnly
                                focusedRowEnabled
                                focusedRowKey={selectedRow?.id}
                                onFocusedRowChanged={handleCellClick}
                            >
                                <Template
                                    name="EditedBySimpleTextColumn"
                                    render={EditedBySimpleTextColumnTemplate}
                                />
                                <Template
                                    name="StateOrderColumn"
                                    render={StateOrderSimpleTextColumnTemplate}
                                />
                                <Template
                                    name="TruckFillingColumn"
                                    render={TruckFillingColumnTemplate}
                                />
                                <Template
                                    name="ForwardStepsLocationsColumn"
                                    render={ForwardStepsLocationsColumnTemplate}
                                />
                                <Template
                                    name="DraftClosedDeadlineColumn"
                                    render={DraftClosedDeadlineColumnTemplate}
                                />
                                <Template
                                    name="ReturnStepsLocationsColumn"
                                    render={ReturnStepsLocationsColumnTemplate}
                                />
                                <Template
                                    name="WmsExternalIdsColumn"
                                    render={WmsExternalIdsColumnTemplate}
                                />
                            </DataGrid>
                        </Item>
                        <Item>
                            <div className={styles.addNewOrderDiv}>
                                <Button
                                    className={styles.addNewOrderButton}
                                    inspectorId={orderScreenButtonId}
                                    onClick={onCreateNewOrder}
                                />
                            </div>
                        </Item>
                    </Box>
                </InspectorVariablesContext>
            </div>
        </Drawer>
    );
});

type OrdersScreenComponentProps = OrdersScreenProps & RouteComponentProps<{}>;

export default OrdersScreenComponent;
